/**
 * Libellés de la page vue d'aigle (ARTO-58), français d'abord puis anglais
 * (D15). Ils vivent dans le namespace `console` : `i18n.ts` les reprend sous
 * la clé `vueDAigle`. Le fichier est à part pour que les écrans de saisie
 * (finances, jalons, associés) puissent éditer `i18n.ts` sans toucher ceux-ci.
 *
 * Voix 2.2 : phrases courtes, jamais « dashboard ». Un montant non saisi se
 * dit « à saisir », jamais « 0 € ».
 */
export const vueDAigleFr = {
  titre: 'Vue d’aigle',
  texte:
    'Situation de la plateforme Artosera au {{date}} : chiffres agrégés, calculés côté plateforme.',
  texteAdmin: 'Les blocs d’exploitation ne s’affichent que pour l’équipe Venio.',
  chargement: 'Chargement de la vue d’aigle…',
  erreur: 'La vue d’aigle n’a pas pu être chargée. Réessayez.',
  lectureSeule: 'Lecture seule',
  voirCommeAssocie: 'Voir comme un associé',
  apercu: {
    titre: 'Aperçu associé',
    texte:
      'Vous voyez cette page comme un associé : lecture seule, sans détail par galerie ni action.',
    revenir: 'Revenir à ma vue',
  },
  periode: {
    aria: 'Période',
    '30j': '30 jours',
    '90j': 'Trimestre',
    '12m': '12 mois',
    note: {
      '30j': 'Courbes et variations sur 30 jours',
      '90j': 'Courbes et variations sur le trimestre',
      '12m': 'Courbes et variations sur 12 mois',
    },
  },
  sommaire: {
    aria: 'Sections de la page',
    aTraiter: 'À traiter',
    revenus: 'Revenus',
    sommesDues: 'Sommes dues',
    parc: 'Parc',
    modules: 'Modules',
    usage: 'Usage',
    sante: 'Santé',
    couts: 'Coûts et marge',
    jalons: 'Jalons',
    partage: 'Partage',
  },
  aSaisir: 'à saisir',
  nonComptees_one: '{{count}} ligne à saisir n’est pas comptée',
  nonComptees_other: '{{count}} lignes à saisir ne sont pas comptées',
  historiqueCourt: 'Historique en cours de constitution',
  gerer: 'Gérer',
  indicateurs: {
    aria: 'Indicateurs',
    mrr: 'Revenu mensuel récurrent (MRR)',
    mrrAide: 'Abonnements et forfaits, hors services ponctuels',
    arr: 'Revenu annualisé (ARR)',
    arrAide: 'MRR × 12',
    production: 'Galeries en production',
    productionAide: 'Abonnement actif, contrat en cours',
    essai: 'Galeries en essai',
    essaiAide: 'Pas encore sous contrat',
    prochaineFinEssai: 'Prochaine fin d’essai le {{date}}',
    marge: 'Marge du mois',
    margeAide: 'MRR moins les coûts du mois',
    utilisateurs: 'Utilisateurs actifs sur 30 jours',
    utilisateursAide: 'Comptes connectés au moins une fois',
    variation: 'vs {{mois}}',
    saisirCouts: 'Saisir les coûts du mois',
  },
  aTraiter: {
    titre: 'À traiter',
    note: 'Réservé à l’exploitation',
    vide: 'Rien à traiter pour l’instant.',
    action: {
      sans_contrat: 'Ouvrir la fiche',
      essai_echu: 'Ouvrir la fiche',
      releve_en_echec: 'Ouvrir la comptabilité',
      creance_en_retard: 'Voir les sommes dues',
      montant_a_saisir: 'Saisir le montant',
      couts_a_saisir: 'Saisir les coûts',
      defaut: 'Ouvrir',
    },
  },
  revenus: {
    titre: 'Revenus',
    courbe: 'MRR sur la période (en euros)',
    courbeTableau: 'Revenu mensuel récurrent sur {{count}} mois, en euros',
    colMois: 'Mois',
    colMrr: 'MRR',
    repartition: 'Répartition du revenu de {{mois}}',
    abonnements: 'Abonnements des galeries',
    forfaits: 'Forfaits exploitant, par tranche de galeries',
    exploitants_one: '{{count}} exploitant',
    exploitants_other: '{{count}} exploitants',
    aucunExploitant: 'aucun exploitant',
    horsGrille: 'Hors grille',
    recurrent: 'Récurrent (MRR)',
    ponctuels: 'Services ponctuels',
    totalMois: 'Revenu du mois',
    noteForfait:
      'Forfait mensuel d’exploitation : un montant par exploitant, selon le nombre de galeries qu’il accueille.',
    gererAbonnements: 'Gérer les abonnements',
    abonnementsTitre: 'Abonnements',
    abonnementsResume_one: '{{count}} abonnement',
    abonnementsResume_other: '{{count}} abonnements',
    colGalerie: 'Galerie',
    colPalier: 'Palier',
    colMontant: 'Montant mensuel',
    colRemise: 'Remise',
    colEtat: 'État',
    colEcheance: 'Échéance d’engagement',
    colAction: 'Action',
    modifier: 'Modifier',
    etat: {
      essai: 'Essai',
      actif: 'Actif',
      suspendu: 'Suspendu',
      resilie: 'Résilié',
    },
    pied: 'Échéances d’engagement les plus proches d’abord',
    afficherTout: 'Afficher les {{count}} abonnements',
    reduire: 'Réduire la liste',
    aucunAbonnement: 'Aucun abonnement enregistré.',
  },
  sommesDues: {
    titre: 'Sommes dues',
    note: 'Créances ouvertes et encaissements du mois',
    due: 'Dû',
    dueAide: 'Créances ouvertes',
    enRetard: 'En retard',
    enRetardAide: 'Échéance dépassée',
    encaisse: 'Encaissé ce mois',
    encaisseAide: 'Paiements reçus ce mois-ci',
    gerer: 'Gérer les sommes dues',
  },
  parc: {
    titre: 'Parc',
    note: 'Nombre de galeries par état, fin de chaque mois',
    graphique: 'Galeries par mois et par état',
    colMois: 'Mois',
    colTotal: 'Total',
    compteurs: 'Le parc en compteurs',
    parEtat: 'Par état · {{count}} galeries',
    parPalier: 'Par palier d’abonnement',
    parTranche: 'Par tranche d’usage',
    palier: 'Palier {{palier}}',
    tranche: {
      '0-2': '0 à 2 modules actifs',
      '3-5': '3 à 5 modules actifs',
      '6+': '6 modules et plus',
    },
    etat: {
      production: 'En production',
      essai: 'En essai',
      sans_contrat: 'Sans contrat',
      suspendue: 'Suspendue',
      resiliee: 'Résiliée',
    },
    serie: {
      production: 'En production',
      essai: 'En essai',
      sansContrat: 'Sans contrat',
      suspendues: 'Suspendues',
      resiliees: 'Résiliées',
    },
    noteAnonyme: 'Compteurs anonymes, calculés côté plateforme : aucun nom de galerie.',
    noteAssocies: 'Vue associé.',
    noteAssociesTexte:
      'Les associés voient le parc sous forme anonyme : les compteurs ci-dessus, sans aucun nom de galerie. Le détail ci-dessous vous est réservé.',
    detail: 'Détail par galerie',
    filtre: 'Filtrer par état',
    toutes: 'Toutes ({{count}})',
    filtreEtat: '{{etat}} ({{count}})',
    colGalerie: 'Galerie',
    colEtat: 'État',
    colPalier: 'Palier',
    colModules: 'Modules',
    colDate: 'Entrée ou fin d’essai',
    entreeLe: 'Entrée le {{date}}',
    finEssaiLe: 'Fin d’essai le {{date}}',
    affichage: 'Affichage : {{n}} sur {{total}} · détail réservé à l’exploitation',
    afficherTout: 'Afficher les {{count}} galeries',
    reduire: 'Réduire la liste',
    aucune: 'Aucune galerie dans cet état.',
  },
  modules: {
    titre: 'Adoption des modules complémentaires',
    note: 'Sur les {{count}} galeries en exploitation, essais compris',
    score: '{{galeries}} sur {{sur}}',
    vide: 'Aucun module complémentaire n’est encore activé.',
  },
  usage: {
    titre: 'Usage agrégé',
    note: 'Totaux de la plateforme',
    utilisateurs: 'Utilisateurs actifs sur 30 jours',
    oeuvres: 'Œuvres gérées',
    stockage: 'Stockage consommé',
    mesureLe: 'Mesuré le {{date}}',
    nonMesure: 'Mesure non disponible',
    nonMesureAide: 'La première mesure n’a pas encore eu lieu.',
  },
  sante: {
    titre: 'Santé',
    note: 'Situation au {{date}}',
    detail: 'Page Santé détaillée',
    statut: { ok: 'Au vert', a_surveiller: 'À surveiller', degrade: 'Dégradé' },
    global: {
      ok: { titre: 'Tout est au vert', texte: 'Aucun point ne demande d’attention.' },
      a_surveiller: {
        titre: 'Un point à surveiller',
        texte: 'Au moins un point demande de l’attention, sans être dégradé.',
      },
      degrade: { titre: 'Service dégradé', texte: 'Au moins un point est dégradé.' },
    },
    feu: { ok: 'Feu vert', a_surveiller: 'Feu orange', degrade: 'Feu rouge' },
    point: {
      base: { titre: 'Base de données', depuis: 'Depuis le {{date}}' },
      stockage: { titre: 'Stockage des fichiers', depuis: 'Depuis le {{date}}' },
      travaux: {
        titre: 'File de travaux',
        depuis: 'Plus ancien travail en attente depuis le {{date}}',
      },
      sauvegardes: { titre: 'Sauvegardes', depuis: 'Dernière sauvegarde réussie : {{date}}' },
    },
    pointDefaut: 'Depuis le {{date}}',
    aucunPoint: 'Aucun point de santé relevé.',
  },
  couts: {
    titre: 'Coûts et marge',
    note: 'Mois de {{mois}}, en euros',
    saisir: 'Saisir les coûts du mois',
    nonSaisis: 'Coûts du mois non saisis',
    caption: 'Coûts du mois par poste',
    colPoste: 'Poste',
    colMontant: 'Montant',
    total: 'Total des coûts',
    mrr: 'Revenu mensuel récurrent',
    marge: 'Marge (MRR moins coûts)',
    taux: 'Taux de marge',
    vide: 'Aucun coût saisi pour ce mois.',
    noteBas: 'Les services ponctuels ne sont pas comptés dans le MRR.',
  },
  jalons: {
    titre: 'Jalons',
    modifier: 'Modifier',
    dateAbsente: 'Date à fixer',
    etat: { atteint: 'Fait', en_cours: 'En cours', a_venir: 'À venir', abandonne: 'Abandonné' },
    vide: 'Aucun jalon publié pour l’instant.',
  },
  partage: {
    titre: 'Partage entre associés',
    noteDirection: 'Parts et versements, mois par mois',
    noteAssocie: 'Votre part et vos versements',
    gerer: 'Gérer les associés',
    part: 'Part : {{pourcent}}',
    partAbsente: 'Part à saisir',
    caption: 'Versements de {{libelle}}',
    colMois: 'Mois',
    colDu: 'Dû',
    colVerse: 'Versé',
    total: 'Total',
  },
};

export const vueDAigleEn: typeof vueDAigleFr = {
  titre: 'Overview',
  texte: 'Artosera platform status on {{date}}: aggregated figures, computed platform-side.',
  texteAdmin: 'Operations blocks only appear for the Venio team.',
  chargement: 'Loading the overview…',
  erreur: 'The overview could not be loaded. Try again.',
  lectureSeule: 'Read-only',
  voirCommeAssocie: 'View as a partner',
  apercu: {
    titre: 'Partner preview',
    texte:
      'You are seeing this page as a partner: read-only, with no per-gallery detail or action.',
    revenir: 'Back to my view',
  },
  periode: {
    aria: 'Period',
    '30j': '30 days',
    '90j': 'Quarter',
    '12m': '12 months',
    note: {
      '30j': 'Curves and changes over 30 days',
      '90j': 'Curves and changes over the quarter',
      '12m': 'Curves and changes over 12 months',
    },
  },
  sommaire: {
    aria: 'Page sections',
    aTraiter: 'To handle',
    revenus: 'Revenue',
    sommesDues: 'Amounts due',
    parc: 'Portfolio',
    modules: 'Modules',
    usage: 'Usage',
    sante: 'Health',
    couts: 'Costs and margin',
    jalons: 'Milestones',
    partage: 'Profit share',
  },
  aSaisir: 'to enter',
  nonComptees_one: '{{count}} line to enter is not counted',
  nonComptees_other: '{{count}} lines to enter are not counted',
  historiqueCourt: 'History still being built up',
  gerer: 'Manage',
  indicateurs: {
    aria: 'Indicators',
    mrr: 'Monthly recurring revenue (MRR)',
    mrrAide: 'Subscriptions and flat fees, excluding one-off services',
    arr: 'Annualised revenue (ARR)',
    arrAide: 'MRR × 12',
    production: 'Galleries in production',
    productionAide: 'Active subscription, contract running',
    essai: 'Galleries on trial',
    essaiAide: 'Not under contract yet',
    prochaineFinEssai: 'Next trial ends on {{date}}',
    marge: 'Margin for the month',
    margeAide: 'MRR minus the month’s costs',
    utilisateurs: 'Active users over 30 days',
    utilisateursAide: 'Accounts that signed in at least once',
    variation: 'vs {{mois}}',
    saisirCouts: 'Enter this month’s costs',
  },
  aTraiter: {
    titre: 'To handle',
    note: 'Operations only',
    vide: 'Nothing to handle for now.',
    action: {
      sans_contrat: 'Open the record',
      essai_echu: 'Open the record',
      releve_en_echec: 'Open the accounts',
      creance_en_retard: 'See amounts due',
      montant_a_saisir: 'Enter the amount',
      couts_a_saisir: 'Enter the costs',
      defaut: 'Open',
    },
  },
  revenus: {
    titre: 'Revenue',
    courbe: 'MRR over the period (in euros)',
    courbeTableau: 'Monthly recurring revenue over {{count}} months, in euros',
    colMois: 'Month',
    colMrr: 'MRR',
    repartition: 'Revenue breakdown for {{mois}}',
    abonnements: 'Gallery subscriptions',
    forfaits: 'Operator flat fees, by gallery band',
    exploitants_one: '{{count}} operator',
    exploitants_other: '{{count}} operators',
    aucunExploitant: 'no operator',
    horsGrille: 'Off-grid',
    recurrent: 'Recurring (MRR)',
    ponctuels: 'One-off services',
    totalMois: 'Revenue for the month',
    noteForfait:
      'Monthly operating flat fee: one amount per operator, depending on the number of galleries they host.',
    gererAbonnements: 'Manage subscriptions',
    abonnementsTitre: 'Subscriptions',
    abonnementsResume_one: '{{count}} subscription',
    abonnementsResume_other: '{{count}} subscriptions',
    colGalerie: 'Gallery',
    colPalier: 'Tier',
    colMontant: 'Monthly amount',
    colRemise: 'Discount',
    colEtat: 'State',
    colEcheance: 'Commitment end',
    colAction: 'Action',
    modifier: 'Edit',
    etat: { essai: 'Trial', actif: 'Active', suspendu: 'Suspended', resilie: 'Terminated' },
    pied: 'Nearest commitment ends first',
    afficherTout: 'Show all {{count}} subscriptions',
    reduire: 'Shorten the list',
    aucunAbonnement: 'No subscription recorded.',
  },
  sommesDues: {
    titre: 'Amounts due',
    note: 'Open receivables and this month’s collections',
    due: 'Due',
    dueAide: 'Open receivables',
    enRetard: 'Overdue',
    enRetardAide: 'Past the due date',
    encaisse: 'Collected this month',
    encaisseAide: 'Payments received this month',
    gerer: 'Manage amounts due',
  },
  parc: {
    titre: 'Portfolio',
    note: 'Number of galleries by state, at the end of each month',
    graphique: 'Galleries by month and by state',
    colMois: 'Month',
    colTotal: 'Total',
    compteurs: 'The portfolio in counters',
    parEtat: 'By state · {{count}} galleries',
    parPalier: 'By subscription tier',
    parTranche: 'By usage band',
    palier: '{{palier}} tier',
    tranche: {
      '0-2': '0 to 2 active modules',
      '3-5': '3 to 5 active modules',
      '6+': '6 modules or more',
    },
    etat: {
      production: 'In production',
      essai: 'On trial',
      sans_contrat: 'No contract',
      suspendue: 'Suspended',
      resiliee: 'Terminated',
    },
    serie: {
      production: 'In production',
      essai: 'On trial',
      sansContrat: 'No contract',
      suspendues: 'Suspended',
      resiliees: 'Terminated',
    },
    noteAnonyme: 'Anonymous counters, computed platform-side: no gallery name.',
    noteAssocies: 'Partner view.',
    noteAssociesTexte:
      'Partners see the portfolio anonymously: the counters above, without any gallery name. The detail below is reserved for you.',
    detail: 'Detail by gallery',
    filtre: 'Filter by state',
    toutes: 'All ({{count}})',
    filtreEtat: '{{etat}} ({{count}})',
    colGalerie: 'Gallery',
    colEtat: 'State',
    colPalier: 'Tier',
    colModules: 'Modules',
    colDate: 'Start or trial end',
    entreeLe: 'Live on {{date}}',
    finEssaiLe: 'Trial ends on {{date}}',
    affichage: 'Showing {{n}} of {{total}} · detail reserved for operations',
    afficherTout: 'Show all {{count}} galleries',
    reduire: 'Shorten the list',
    aucune: 'No gallery in this state.',
  },
  modules: {
    titre: 'Add-on module adoption',
    note: 'Out of the {{count}} galleries in operation, trials included',
    score: '{{galeries}} of {{sur}}',
    vide: 'No add-on module is switched on yet.',
  },
  usage: {
    titre: 'Aggregated usage',
    note: 'Platform totals',
    utilisateurs: 'Active users over 30 days',
    oeuvres: 'Artworks managed',
    stockage: 'Storage used',
    mesureLe: 'Measured on {{date}}',
    nonMesure: 'Measurement unavailable',
    nonMesureAide: 'The first measurement has not run yet.',
  },
  sante: {
    titre: 'Health',
    note: 'Status on {{date}}',
    detail: 'Detailed Health page',
    statut: { ok: 'Green', a_surveiller: 'Watch', degrade: 'Degraded' },
    global: {
      ok: { titre: 'All green', texte: 'No point needs attention.' },
      a_surveiller: {
        titre: 'One point to watch',
        texte: 'At least one point needs attention without being degraded.',
      },
      degrade: { titre: 'Degraded service', texte: 'At least one point is degraded.' },
    },
    feu: { ok: 'Green light', a_surveiller: 'Amber light', degrade: 'Red light' },
    point: {
      base: { titre: 'Database', depuis: 'Since {{date}}' },
      stockage: { titre: 'File storage', depuis: 'Since {{date}}' },
      travaux: { titre: 'Job queue', depuis: 'Oldest waiting job since {{date}}' },
      sauvegardes: { titre: 'Backups', depuis: 'Last successful backup: {{date}}' },
    },
    pointDefaut: 'Since {{date}}',
    aucunPoint: 'No health point reported.',
  },
  couts: {
    titre: 'Costs and margin',
    note: '{{mois}}, in euros',
    saisir: 'Enter this month’s costs',
    nonSaisis: 'Costs for the month not entered',
    caption: 'Costs for the month, by item',
    colPoste: 'Item',
    colMontant: 'Amount',
    total: 'Total costs',
    mrr: 'Monthly recurring revenue',
    marge: 'Margin (MRR minus costs)',
    taux: 'Margin rate',
    vide: 'No cost entered for this month.',
    noteBas: 'One-off services are not counted in MRR.',
  },
  jalons: {
    titre: 'Milestones',
    modifier: 'Edit',
    dateAbsente: 'Date to be set',
    etat: { atteint: 'Done', en_cours: 'In progress', a_venir: 'Upcoming', abandonne: 'Dropped' },
    vide: 'No milestone published yet.',
  },
  partage: {
    titre: 'Profit share between partners',
    noteDirection: 'Shares and payouts, month by month',
    noteAssocie: 'Your share and your payouts',
    gerer: 'Manage partners',
    part: 'Share: {{pourcent}}',
    partAbsente: 'Share to enter',
    caption: 'Payouts for {{libelle}}',
    colMois: 'Month',
    colDu: 'Due',
    colVerse: 'Paid',
    total: 'Total',
  },
};
